import { useEffect, useRef } from 'react'
import type React from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextChain } from '@/api/types/stowed-element'
import { isPhotoDropTarget } from '@/lib/photos/fill-shape-with-photo'
import { isShapeLockedForUserEdit } from '@/lib/groups/element-edit-policy'
import type { PlacementCanvasApi } from '@/hooks/use-placement-session'

type PlacementRegistration = {
  registerCanvasApi: (api: PlacementCanvasApi | null) => void
}

export type UsePlacementCanvasBridgeArgs = {
  /** The placement context for this item kind; null when no provider is mounted. */
  placement: PlacementRegistration | null
  toCanvasPoint: PlacementCanvasApi['toCanvasPoint']
  isOverCanvas: PlacementCanvasApi['isOverCanvas']
  bleedWidth: number
  bleedHeight: number
  shapeManagerRef: React.RefObject<ShapeManagerRef | null>
  /** Live text-link chains, so a chain member is reported as blocked rather than fillable. */
  linkedTextChains?: TextChain[]
  /** The item kind's own rule for "this shape is a linked text frame, refuse the drop". */
  isBlockedByTextChain: (shape: ShapeData, chains: TextChain[]) => boolean
  /**
   * Whether a shape can be a fill target for this item kind. Defaults to `isPhotoDropTarget`
   * (kindId 1/2/10 and closed pen paths). Override for item kinds that fill a broader set of
   * shapes — e.g. color swatches, which can fill any fillable shape including ovals.
   */
  isDropTarget?: (shape: ShapeData) => boolean
  /** Whether a shape under the cursor can receive the item (fill it). Defaults to true. An item
   *  that is only ever placed as new elements (a template) passes false, so nothing under the
   *  cursor is a target and a drop never fills, blocks or falls through to a shape. */
  acceptsShapeDrops?: boolean
}

/**
 * Wires the canvas side of a tray-item placement session: keeps a stable `PlacementCanvasApi`
 * whose geometry follows the latest props, answers the drop-target hit test, and registers itself
 * with the provider. Shared by `PhotoPlacementCanvasBridge` and `ClikArtPlacementCanvasBridge`,
 * which differ only in which provider they register with and which text-chain policy they apply.
 */
export function usePlacementCanvasBridge({
  placement,
  toCanvasPoint,
  isOverCanvas,
  bleedWidth,
  bleedHeight,
  shapeManagerRef,
  linkedTextChains,
  isBlockedByTextChain,
  isDropTarget = isPhotoDropTarget,
  acceptsShapeDrops = true,
}: UsePlacementCanvasBridgeArgs): void {
  const shapeManagerRefLocal = useRef(shapeManagerRef)
  const linkedTextChainsLocal = useRef(linkedTextChains)
  const isBlockedByTextChainLocal = useRef(isBlockedByTextChain)
  const isDropTargetLocal = useRef(isDropTarget)
  const acceptsShapeDropsLocal = useRef(acceptsShapeDrops)

  shapeManagerRefLocal.current = shapeManagerRef
  linkedTextChainsLocal.current = linkedTextChains
  isBlockedByTextChainLocal.current = isBlockedByTextChain
  isDropTargetLocal.current = isDropTarget
  acceptsShapeDropsLocal.current = acceptsShapeDrops

  const apiRef = useRef<PlacementCanvasApi>({
    toCanvasPoint,
    isOverCanvas,
    bleedWidth,
    bleedHeight,
    hitFillableShapeAtPoint: (_clientX, _clientY) => null,
  })

  apiRef.current.toCanvasPoint = toCanvasPoint
  apiRef.current.isOverCanvas = isOverCanvas
  apiRef.current.bleedWidth = bleedWidth
  apiRef.current.bleedHeight = bleedHeight

  // Geometry-based hit test so the drag ghost (pointer-events:none) doesn't interfere.
  apiRef.current.hitFillableShapeAtPoint = (clientX, clientY) => {
    if (!acceptsShapeDropsLocal.current) return null

    const mgr = shapeManagerRefLocal.current.current
    const chains = linkedTextChainsLocal.current ?? []
    const shapes = mgr?.getShapes() ?? []
    const shapesById = new Map(shapes.map((s) => [s.id, s]))

    if (!mgr) return null

    const shapeId = mgr.hitShapeIdAtClientPoint(clientX, clientY)

    if (!shapeId) return null

    const shape = shapesById.get(shapeId)

    if (!shape || !isDropTargetLocal.current(shape)) return null

    // A chain member is returned *flagged*, not dropped, so the drop is refused outright
    // (WMPCM-8516). Returning null would instead let the drop fall through to the empty-canvas
    // branch and place a standalone item on top of the frame — the locked-shape behaviour
    // below, which is deliberately not what a linked frame does.
    //
    // Checked ahead of the lock test on purpose: a locked chain member is still a linked text
    // frame, and the story is about the frame, not about who may edit it. Deferring to the lock
    // rule would silently give locked and unlocked chain members two different outcomes for the
    // same gesture.
    //
    // One branch covers what used to be two: since text-unification a Text-tool frame IS a
    // shape, so `hitShapeIdAtClientPoint` already returns it (frontmost-first).
    if (isBlockedByTextChainLocal.current(shape, chains)) {
      return { shapeId, kindId: shape.kindId ?? 0, blockedReason: 'linked-text-frame' }
    }

    // Locked shapes (or shapes under a locked group ancestor) cannot be filled — matches
    // legacy's findObject2, which skips locked sprites during drag hit-testing entirely, so
    // a locked shape can never be a drop target (the drop falls back to standalone
    // placement). Frozen-but-unlocked shapes remain valid targets — freezing only blocks
    // geometry, not content.
    if (isShapeLockedForUserEdit(shape, shapesById)) return null

    return { shapeId, kindId: shape.kindId ?? 0 }
  }

  useEffect(() => {
    if (!placement) return

    placement.registerCanvasApi(apiRef.current)
    return () => placement.registerCanvasApi(null)
  }, [placement])
}
